/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { createAnalyticsStore } from 'src/luis/core/stores/analyticsStore';
import { createBatchTestStoreHouse } from 'src/luis/core/stores/batchTestStoreHouse';
import { createEntityStoreHouse } from 'src/luis/core/stores/entityStoreHouse';
import { createIntentStoreHouse } from 'src/luis/core/stores/intentStoreHouse';
import { createPatternStoreHouse } from 'src/luis/core/stores/patternStoreHouse';
import { createPhraseListStoreHouse } from 'src/luis/core/stores/phraseListStoreHouse';
import { createTestPanePredictionStoreHouse } from 'src/luis/core/stores/testPanePredictionStoreHouse';
import { TrainingStore } from 'src/luis/core/stores/TrainingStore';
import { AppVersionSettings } from 'src/luis/projects/apps/types/AppVersionSettings';
import { createPredictionStoreHouse } from 'src/luis/projects/utterances/stores/predictionStoreHouse';
import { createSuggestedUtteranceStoreHouse } from 'src/luis/projects/utterances/stores/suggestedUtteranceStoreHouse';
import { LuisId } from 'src/luis/types/Resource';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';

/**
 * An app version store factory object.
 *
 * @param id The id of the app version to create.
 * @param intent The app version data.
 */
export const createAppVersionStore = (id: LuisId, appVersion: Partial<AppVersionStore>): AppVersionStore => {
    const store: AppVersionStore = {
        id: `${id}`,
        isDirty: appVersion.isDirty,
        settings: appVersion.settings,
        createdDate: appVersion.createdDate,
        trainedDate: appVersion.trainedDate,
        intentsCount: appVersion.intentsCount,
        modifiedDate: appVersion.modifiedDate,
        entitiesCount: appVersion.entitiesCount,
        publishedDate: appVersion.publishedDate,
        endpointHitsCount: appVersion.endpointHitsCount,
        trainingStore: appVersion.trainingStore ?? new TrainingStore(),

        analyticsStore: createAnalyticsStore({}),
        intentStoreHouse: createIntentStoreHouse(),
        entityStoreHouse: createEntityStoreHouse(),
        patternStoreHouse: createPatternStoreHouse(),
        batchTestStoreHouse: createBatchTestStoreHouse(),
        phraseListStoreHouse: createPhraseListStoreHouse(),
        predictionStoreHouse: createPredictionStoreHouse(),
        testPanePredictionStoreHouse: createTestPanePredictionStoreHouse(),
        suggestedUtteranceStoreHouse: createSuggestedUtteranceStoreHouse(),

        /**
         * A flag whether the app version is trained or not.
         */
        get isTrained() {
            return this.trainingStore.isTrained;
        },

        /**
         * Renames the app version.
         * For app version, name and id are the same.
         *
         * @param version The new version name.
         */
        rename(version: string): void {
            this.id = version;
        },

        /**
         * Sets app version store dirty state.
         *
         * @param isDirty The new dirty state.
         */
        setDirty(isDirty: boolean): void {
            this.isDirty = isDirty;
        },

        /**
         * Sets app version entities count.
         *
         * @param count The entities count.
         */
        setEntitiesCount(count: number): void {
            this.entitiesCount = count;
        },

        /**
         * Sets app version intents count.
         *
         * @param count The entities count.
         */
        setIntentsCount(count: number): void {
            this.intentsCount = count;
        },

        /**
         * Sets app version modified date.
         *
         * @param modifiedDate The modified date of the app version.
         */
        setModifiedDate(modifiedDate: Date): void {
            this.modifiedDate = modifiedDate;
        },

        /**
         * Sets app version published date.
         *
         * @param publishedDate The published date of the app version.
         */
        setPublishedDate(publishedDate: Date): void {
            this.publishedDate = publishedDate;
        },

        /**
         * Sets app version trained date.
         *
         * @param trainedDate The trained date of the app version.
         */
        setTrainedDate(trainedDate: Date): void {
            this.trainedDate = trainedDate;
        },

        /**
         * Sets app version settings.
         *
         * @param settings The settings of the app version.
         */
        setSettings(settings: AppVersionSettings): void {
            this.settings = settings;
        }
    };

    return observable<AppVersionStore>(
        store,
        {
            entitiesCount: observable,
            intentsCount: observable,
            isDirty: observable,
            modifiedDate: observable,
            publishedDate: observable,
            trainedDate: observable,
            settings: observable,
            isTrained: computed,
            setDirty: action,
            setEntitiesCount: action,
            setIntentsCount: action,
            setModifiedDate: action,
            setPublishedDate: action,
            setTrainedDate: action,
            setSettings: action,
            rename: action
        },
        { deep: false }
    );
};
